import {
  createContext,
  Dispatch,
  FC,
  PropsWithChildren,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { ToggleFn, useToggle } from '@revolut-internal/ui-kit'

import {
  AssistantChatMessageFeedback,
  AssistantConversationOut,
  AssistantNoConversationOut,
  AuthorRole,
  GetFixedPromptTextGenerationOutState,
  Message,
} from 'lib/apiClient'

import { AssistantChatMessagesHook, useAssistantChatMessages } from '../hooks'

export interface AssistantChatMessagesContextState extends AssistantChatMessagesHook {
  memoryResetActive: boolean
  setMemoryResetActive: ToggleFn
  previousSnapshotId: string | null
  setPreviousSnapshotId: Dispatch<SetStateAction<string | null>>
  chatMessagesFeedbackMap: Map<string, AssistantChatMessageFeedback>
  setChatMessagesFeedbackMap: (
    newFeedbackMap: Map<string, AssistantChatMessageFeedback>,
  ) => void
  getChatMessageFeedbackById: (id: string) => AssistantChatMessageFeedback | null
  addAssistantChatMessageFeedbackById: (
    id: string,
    feedback: AssistantChatMessageFeedback,
  ) => void
  removeAssistantChatMessageFeedbackById: (id: string) => void
  initialData: AssistantConversationOut | AssistantNoConversationOut | undefined
}

interface AssistantChatMessagesProviderProps {
  initialData: AssistantConversationOut | AssistantNoConversationOut | undefined
}

export const AssistantChatMessagesContext =
  createContext<AssistantChatMessagesContextState | null>(null)

export const AssistantChatMessagesProvider: FC<
  PropsWithChildren<AssistantChatMessagesProviderProps>
> = ({ initialData, children }) => {
  const [chatMessagesFeedbackMap, setChatMessagesFeedbackMap] = useState<
    Map<string, AssistantChatMessageFeedback>
  >(new Map<string, AssistantChatMessageFeedback>())

  const handleSetChatMessagesFeedbackMap = useCallback(
    (newFeedbackMap: Map<string, AssistantChatMessageFeedback>) => {
      setChatMessagesFeedbackMap(newFeedbackMap)
    },
    [],
  )

  const [memoryResetActive, setMemoryResetActive] = useToggle({ defaultState: false })
  const [previousSnapshotId, setPreviousSnapshotId] = useState<string | null>(null)

  useEffect(() => {
    setPreviousSnapshotId(initialData?.dialogueSnapshotId ?? null)
  }, [initialData])

  const initialAssistantResponseMessage: Message | null = useMemo(() => {
    if (initialData?.state === GetFixedPromptTextGenerationOutState.Generated) {
      return {
        messageId: initialData.id,
        authorRole: AuthorRole.Agent,
        created: initialData.created,
        text: initialData.result.response.text,
      } as Message
    }
    return null
  }, [initialData])

  const assistantChatMessagesState = useAssistantChatMessages({
    correlationId: initialData?.correlationId ?? null,
    initialAssistantResponseMessage,
  })

  const getChatMessageFeedbackById = useCallback(
    (id: string): AssistantChatMessageFeedback | null => {
      return chatMessagesFeedbackMap.get(id) ?? null
    },
    [chatMessagesFeedbackMap],
  )

  const addAssistantChatMessageFeedbackById = useCallback(
    (id: string, feedback: AssistantChatMessageFeedback) => {
      const tempFeedbackMap = new Map([[id, feedback]])
      setChatMessagesFeedbackMap(
        new Map([...chatMessagesFeedbackMap, ...tempFeedbackMap]),
      )
    },
    [chatMessagesFeedbackMap],
  )

  const removeAssistantChatMessageFeedbackById = useCallback(
    (id: string) => {
      const updatedFeedbackMap = new Map(chatMessagesFeedbackMap)
      updatedFeedbackMap.delete(id)
      setChatMessagesFeedbackMap(new Map(updatedFeedbackMap))
    },
    [chatMessagesFeedbackMap],
  )

  const memoChatMessagesFeedbackMap = useMemo(
    () => chatMessagesFeedbackMap,
    [chatMessagesFeedbackMap],
  )

  return (
    <AssistantChatMessagesContext.Provider
      value={{
        memoryResetActive,
        setMemoryResetActive,
        previousSnapshotId,
        setPreviousSnapshotId,
        initialData,
        chatMessagesFeedbackMap: memoChatMessagesFeedbackMap,
        setChatMessagesFeedbackMap: handleSetChatMessagesFeedbackMap,
        addAssistantChatMessageFeedbackById,
        removeAssistantChatMessageFeedbackById,
        getChatMessageFeedbackById,
        ...assistantChatMessagesState,
      }}
    >
      {children}
    </AssistantChatMessagesContext.Provider>
  )
}
